import * as APIHelper from "services/api"
import * as endPoint from "shared/constants/endpoint"

const toNum = value => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, ""))
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const pickAny = (source, keys = []) => {
  if (!source || typeof source !== "object") return undefined
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key]
  }
  return undefined
}

const pickNumber = (source, keys = [], fallback = 0) => {
  const raw = pickAny(source, keys)
  const parsed = toNum(raw)
  return parsed ?? fallback
}

const asArray = value => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.resource)) return value.resource
  if (Array.isArray(value?.Resource)) return value.Resource
  if (Array.isArray(value?.data)) return value.data
  return []
}

const requestDashboard = async (url, params = {}) => {
  try {
    return await APIHelper.getRequest(url, { params })
  } catch (_) {
    return await APIHelper.getRequest(url)
  }
}

const firstObject = value => {
  if (!value) return null
  if (Array.isArray(value)) return value[0] || null
  if (value.resource && !Array.isArray(value.resource)) return value.resource
  if (value.Resource && !Array.isArray(value.Resource)) return value.Resource
  if (value.data && !Array.isArray(value.data)) return value.data
  return value
}

const rowLooksLikeOutbound = row => !!row && typeof row === "object" && (row.year_month || row.YearMonth || row.b2c_orders !== undefined || row.B2C_Orders !== undefined)

const rowLooksLikeInbound = row => !!row && typeof row === "object" && (row.year_month || row.YearMonth || row.lcl_shipments !== undefined || row.LCL_Shipments !== undefined)

const mapOutboundRow = row => ({
  year_month: row.year_month || row.YearMonth || row.yearMonth || "",
  latest_date: row.latest_date || row.LatestDate || row.latestDate || null,
  b2c_orders: pickNumber(row, ["b2c_orders", "B2C_Orders"], 0),
  b2c_units: pickNumber(row, ["b2c_units", "B2C_Units"], 0),
  b2b_orders: pickNumber(row, ["b2b_orders", "B2B_Orders"], 0),
  b2b_units: pickNumber(row, ["b2b_units", "B2B_Units"], 0),
  b2c_despatched_orders: pickNumber(row, ["b2c_despatched_orders", "B2C_Despatched_Orders"], 0),
  b2c_despatched_units: pickNumber(row, ["b2c_despatched_units", "B2C_Despatched_Units"], 0),
  b2b_despatched_orders: pickNumber(row, ["b2b_despatched_orders", "B2B_Despatched_Orders"], 0),
  b2b_despatched_units: pickNumber(row, ["b2b_despatched_units", "B2B_Despatched_Units"], 0),
  total_orders: pickNumber(row, ["total_orders", "Total_Orders"], 0),
  total_units: pickNumber(row, ["total_units", "Total_Units"], 0),
  source: pickAny(row, ["source", "Source"]) || "DWH",
})

const mapOutboundSummaryRows = rows => {
  const grouped = new Map()

  rows.forEach(row => {
    const yearMonth = normalizeYearMonth(row.YEAR_MONTH_KEY || row.year_month || row.YearMonth)
    if (!yearMonth) return

    const channel = String(row.CHANNEL || row.channel || "").toUpperCase()
    const current = grouped.get(yearMonth) || {
      year_month: yearMonth,
      latest_date: row.MONTH_END_DATE || row.Month_End_Date || null,
      b2c_orders: 0,
      b2c_units: 0,
      b2b_orders: 0,
      b2b_units: 0,
      b2c_despatched_orders: 0,
      b2c_despatched_units: 0,
      b2b_despatched_orders: 0,
      b2b_despatched_units: 0,
      total_orders: 0,
      total_units: 0,
      source: "DWH",
    }
    const createdOrders = pickNumber(row, ["CREATED_ORDERS", "created_orders"], 0)
    const createdUnits = pickNumber(row, ["CREATED_UNITS", "created_units"], 0)
    const despatchedOrders = pickNumber(row, ["DESPATCHED_ORDERS", "despatched_orders"], 0)
    const despatchedUnits = pickNumber(row, ["DESPATCHED_UNITS", "despatched_units"], 0)

    if (channel === "B2C") {
      current.b2c_orders += createdOrders
      current.b2c_units += createdUnits
      current.b2c_despatched_orders += despatchedOrders
      current.b2c_despatched_units += despatchedUnits
    } else if (channel === "B2B") {
      current.b2b_orders += createdOrders
      current.b2b_units += createdUnits
      current.b2b_despatched_orders += despatchedOrders
      current.b2b_despatched_units += despatchedUnits
    }

    current.total_orders += createdOrders
    current.total_units += createdUnits
    grouped.set(yearMonth, current)
  })

  return sortByMonth([...grouped.values()])
}

const mapInboundRow = row => ({
  year_month: row.year_month || row.YearMonth || row.yearMonth || "",
  lcl_shipments: pickNumber(row, ["lcl_shipments", "LCL_Shipments"], 0),
  lcl_units: pickNumber(row, ["lcl_units", "LCL_Units"], 0),
  fcl_20_shipments: pickNumber(row, ["fcl_20_shipments", "FCL_20_Shipments"], 0),
  fcl_20_units: pickNumber(row, ["fcl_20_units", "FCL_20_Units"], 0),
  fcl_40_shipments: pickNumber(row, ["fcl_40_shipments", "FCL_40_Shipments"], 0),
  fcl_40_units: pickNumber(row, ["fcl_40_units", "FCL_40_Units"], 0),
  returns_new: pickNumber(row, ["returns_new", "Returns_New"], 0),
  returns_new_units: pickNumber(row, ["returns_new_units", "Returns_New_Units"], 0),
  returns_dmg: pickNumber(row, ["returns_dmg", "Returns_Dmg"], 0),
  returns_dmg_units: pickNumber(row, ["returns_dmg_units", "Returns_Dmg_Units"], 0),
})

const normalizeYearMonth = value => {
  const text = String(value || "")
  if (/^\d{6}$/.test(text)) return `${text.slice(0, 4)}-${text.slice(4)}`
  if (/^\d{4}-\d{2}$/.test(text)) return text
  return ""
}

const mapInboundSummaryRows = rows => {
  const grouped = new Map()

  rows.forEach(row => {
    const yearMonth = normalizeYearMonth(row.Year_Month || row.year_month || row.YearMonth)
    if (!yearMonth) return

    const type = String(row.Receipt_Type_Group_Code || row.Receipt_Type || "")
      .toUpperCase()
      .replace(/[\s_-]/g, "")
    const current = grouped.get(yearMonth) || {
      year_month: yearMonth,
      lcl_shipments: 0,
      lcl_units: 0,
      fcl_20_shipments: 0,
      fcl_20_units: 0,
      fcl_40_shipments: 0,
      fcl_40_units: 0,
      returns_new: 0,
      returns_new_units: 0,
      returns_dmg: 0,
      returns_dmg_units: 0,
    }
    const shipments = pickNumber(row, ["Closed_Shipments", "closed_shipments"], 0)
    const units = pickNumber(row, ["Closed_Units", "closed_units"], 0)

    if (type === "LCL") {
      current.lcl_shipments += shipments
      current.lcl_units += units
    } else if (type === "RETURNS") {
      current.returns_new += shipments
      current.returns_new_units += units
    } else if (type.includes("20")) {
      current.fcl_20_shipments += shipments
      current.fcl_20_units += units
    } else if (type.includes("40")) {
      current.fcl_40_shipments += shipments
      current.fcl_40_units += units
    }

    grouped.set(yearMonth, current)
  })

  return sortByMonth([...grouped.values()])
}

const sortByMonth = rows => rows.slice().sort((a, b) => String(a.year_month).localeCompare(String(b.year_month)))

const periodTimeSpan = {
  today: "Last 24 Hours",
  "7d": "Last 7 Days",
  "30d": "Last 30 Days",
  month: "This Month",
}

const periodLabels = {
  today: "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  month: "This month",
}

const getPeriodRow = (response, collectionName, period, periodField = "TimeSpan") => {
  const rows = response?.[collectionName] || response?.[collectionName.toUpperCase()]
  if (!Array.isArray(rows)) return null

  if (rows.length === 1 && !rows[0]?.[periodField] && !rows[0]?.[periodField.toLowerCase()]) return rows[0]

  const expectedTimeSpan = periodTimeSpan[period] || periodTimeSpan.today
  return rows.find(row => String(row[periodField] || row[periodField.toLowerCase()] || "").toLowerCase() === expectedTimeSpan.toLowerCase()) || null
}

class DashboardBusiness {
  getOrdersData = async (company, period = "today") => {
    try {
      const orderRes = await requestDashboard(endPoint.dashboard.order, {})
      return this.mapOrdersData(orderRes, period)
    } catch (_) {
      return this.mapOrdersData({}, period)
    }
  }

  getReceiptsData = async (company, period = "today") => {
    try {
      const receiptRes = await requestDashboard(endPoint.dashboard.receipt, {})
      const receipt = firstObject(receiptRes) || {}
      return this.mapReceiptsData(receipt, period)
    } catch (_) {
      return this.mapReceiptsData({}, period)
    }
  }

  mapOrdersData = (orderRes, period) => {
    const orderPeriod = getPeriodRow(orderRes, "orders", period)
    const unitsPeriod = getPeriodRow(orderRes, "orders_units", period)
    const statusPeriod = getPeriodRow(orderRes, "orders_status", period)
    const exceptionsPeriod = getPeriodRow(orderRes, "exceptions", period)

    return {
      periodLabel: periodLabels[period] || periodLabels.today,
      ordersCreated: orderPeriod ? pickNumber(orderPeriod, ["Created", "created"], 0) : 0,
      ordersDespatched: orderPeriod ? pickNumber(orderPeriod, ["Despatched", "despatched"], 0) : 0,
      unitsCreated: unitsPeriod ? pickNumber(unitsPeriod, ["Created", "created"], 0) : 0,
      unitsDespatched: unitsPeriod ? pickNumber(unitsPeriod, ["Despatched", "despatched"], 0) : 0,
      orderStatus: statusPeriod
        ? {
            "In Pool": pickNumber(statusPeriod, ["InPool", "inPool", "in_pool"], 0),
            Processing: pickNumber(statusPeriod, ["Processing", "processing"], 0),
            "On Hold": pickNumber(statusPeriod, ["OnHold", "onHold", "on_hold"], 0),
            Exceptions: pickNumber(statusPeriod, ["Exceptions", "exceptions"], 0),
          }
        : { "In Pool": 0, Processing: 0, "On Hold": 0, Exceptions: 0 },
      exceptionReasons: exceptionsPeriod
        ? [
            { reason: "No/Ineligible Carrier Error", count: pickNumber(exceptionsPeriod, ["NoCarrierError", "noCarrierError", "no_carrier_error"], 0) },
            { reason: "On Hold", count: pickNumber(exceptionsPeriod, ["OnHold", "onHold", "on_hold"], 0) },
            { reason: "Over-ordered", count: pickNumber(exceptionsPeriod, ["OverOrdered", "overOrdered", "over_ordered"], 0) },
            { reason: "Address Validation Error", count: pickNumber(exceptionsPeriod, ["AddressValidationError", "addressValidationError", "address_validation_error"], 0) },
          ].sort((a, b) => b.count - a.count)
        : [
            { reason: "No/Ineligible Carrier Error", count: 0 },
            { reason: "On Hold", count: 0 },
            { reason: "Over-ordered", count: 0 },
            { reason: "Address Validation Error", count: 0 },
          ],
    }
  }

  mapReceiptsData = (receipt, period) => {
    const closedPeriod = getPeriodRow(receipt, "receipts_closed", period, "period")
    const pending = pickAny(receipt, ["receipts_pending", "ReceiptsPending", "pending_receipts", "PendingReceipts"])
    return {
      periodLabel: periodLabels[period] || periodLabels.today,
      closedReceipts: closedPeriod ? pickNumber(closedPeriod, ["closed_receipts", "ClosedReceipts"], 0) : pickNumber(receipt, ["closed_receipts", "ClosedReceipts", "ReceiptsClosed"], 0),
      inboundUnits: closedPeriod ? pickNumber(closedPeriod, ["units_received", "UnitsReceived"], 0) : pickNumber(receipt, ["inbound_units", "InboundUnits", "units_received", "UnitsReceived"], 0),
      pendingReceipts:
        Array.isArray(pending) && pending.length
          ? pending
              .map(row => ({
                asn: pickAny(row, ["Receipt / ASN", "receipt_asn", "asn", "ASN", "receipt", "Receipt"]) || "-",
                unitsPending: pickNumber(row, ["UnitsPending", "units_pending", "units", "Units"], 0),
              }))
              .sort((a, b) => b.unitsPending - a.unitsPending)
          : [],
    }
  }

  getSlowMovingData = async company => {
    try {
      const response = await requestDashboard(endPoint.dashboard.slow_moving_lines, {})
      return asArray(response).map(row => {
        const daysSinceSale = pickNumber(row, ["DAYS_SINCE_LAST_SOLD", "days_since_last_sold"], 0)
        const neverSold = Boolean(pickAny(row, ["NEVER_SOLD", "never_sold"]))

        return {
          sku: pickAny(row, ["ITEM", "item", "SKU", "sku"]) || "-",
          onHand: pickNumber(row, ["ON_HAND_QTY", "on_hand_qty"], 0),
          sold90d: pickNumber(row, ["SOLD_90D", "sold_90d"], 0),
          daysSinceSale,
          status: neverSold || daysSinceSale >= 365 ? "stagnant" : daysSinceSale >= 90 ? "slow" : "current",
        }
      })
    } catch (_) {
      return []
    }
  }

  getLowestStockData = async company => {
    try {
      const response = await requestDashboard(endPoint.dashboard.lowest_days_of_stock, {})
      return asArray(response).map(row => {
        const daysOfStock = pickNumber(row, ["Days_Cover", "days_cover", "daysOfStock"], 0)

        return {
          sku: pickAny(row, ["ITEM", "item", "SKU", "sku"]) || "-",
          description: pickAny(row, ["Item_Desc", "item_desc", "Description", "description"]) || "-",
          onHand: pickNumber(row, ["On_Hand_Qty", "on_hand_qty", "OnHand", "onHand"], 0),
          daysOfStock,
          status: daysOfStock < 7 ? "red" : daysOfStock < 14 ? "amber" : "green",
        }
      })
    } catch (_) {
      return []
    }
  }

  getOutboundVolumePayload = async company => {
    try {
      const outboundRes = await requestDashboard(endPoint.dashboard.analytics_outbound, {})
      const rawOutboundRows = asArray(outboundRes)
      const outboundRows = rawOutboundRows.some(row => row.YEAR_MONTH_KEY !== undefined || row.MONTH_START_DATE !== undefined)
        ? mapOutboundSummaryRows(rawOutboundRows)
        : rawOutboundRows.filter(rowLooksLikeOutbound).map(mapOutboundRow)
      const outboundMeta = firstObject(outboundRes) || {}
      const info = {
        company_code: pickAny(outboundMeta, ["company_code", "Company_Code", "COMPANY"]) || "",
        wms_name: pickAny(outboundMeta, ["wms_name", "WMS_Name", "COMPANY"]) || "",
        xero_name: pickAny(outboundMeta, ["xero_name", "XERO_Name", "COMPANY"]) || "",
        hs_name: pickAny(outboundMeta, ["hs_name", "HS_Name", "COMPANY"]) || "",
      }

      return {
        info,
        outbound: outboundRows,
        dwh_last_date: pickAny(outboundMeta, ["dwh_last_date", "DWH_Last_Date", "LOAD_DATETIME"]) || null,
        dwh_unavailable: Boolean(pickAny(outboundMeta, ["dwh_unavailable", "DWH_Unavailable"]) ?? false),
      }
    } catch (_) {
      return { info: { company_code: "", wms_name: "", xero_name: "", hs_name: "" }, outbound: [], dwh_last_date: null, dwh_unavailable: true }
    }
  }

  getInboundVolumePayload = async company => {
    try {
      const inboundRes = await requestDashboard(endPoint.dashboard.analytics_inbound, {})
      const rawInboundRows = asArray(inboundRes)
      const inboundRows = rawInboundRows.some(row => row.Year_Month !== undefined || row.Month_Start_Date !== undefined)
        ? mapInboundSummaryRows(rawInboundRows)
        : rawInboundRows.filter(rowLooksLikeInbound)
      const inboundMeta = firstObject(inboundRes) || {}
      const info = {
        company_code: pickAny(inboundMeta, ["company_code", "Company_Code"]) || "",
        wms_name: pickAny(inboundMeta, ["wms_name", "WMS_Name"]) || "",
        xero_name: pickAny(inboundMeta, ["xero_name", "XERO_Name"]) || "",
        hs_name: pickAny(inboundMeta, ["hs_name", "HS_Name"]) || "",
      }

      return {
        info,
        inbound: inboundRows.length ? sortByMonth(inboundRows.map(mapInboundRow)) : [],
        dwh_last_date: pickAny(inboundMeta, ["dwh_last_date", "DWH_Last_Date"]) || null,
        dwh_unavailable: Boolean(pickAny(inboundMeta, ["dwh_unavailable", "DWH_Unavailable"]) ?? false),
      }
    } catch (_) {
      return { info: { company_code: "", wms_name: "", xero_name: "", hs_name: "" }, inbound: [], dwh_last_date: null, dwh_unavailable: true }
    }
  }
}

export const dashboardBusiness = new DashboardBusiness()
export const homeBusiness = dashboardBusiness
export const analyticsBusiness = dashboardBusiness
